import React from 'react';
import { Home, Loader2 } from 'lucide-react';

const LoadingSpinner = ({
                            size = 'default',
                            message = 'Loading...',
                            overlay = false,
                            fullScreen = false,
                            variant = 'default'
                        }) => {
    const sizeClasses = {
        sm: 'w-4 h-4',
        default: 'w-8 h-8',
        lg: 'w-12 h-12',
        xl: 'w-16 h-16'
    };

    const variants = {
        default: 'text-highland-500',
        white: 'text-white',
        gray: 'text-soil-600',
        success: 'text-highland-500',
        warning: 'text-dawn-600',
        error: 'text-murram-500'
    };

    // Simple spinner for inline use
    if (!overlay && !fullScreen) {
        return (
            <div className="flex items-center justify-center space-x-2">
                <Loader2 className={`${sizeClasses[size]} ${variants[variant]} animate-spin`} />
                {message && (
                    <span className={`text-sm ${variant === 'white' ? 'text-white' : 'text-soil-600'}`}>
            {message}
          </span>
                )}
            </div>
        );
    }

    // Full screen loading
    if (fullScreen) {
        return (
            <div className="fixed inset-0 bg-white flex flex-col items-center justify-center z-50">
                {/* App Logo */}
                <div className="mb-8">
                    <div className="w-16 h-16 bg-highland-500 rounded-2xl flex items-center justify-center">
                        <Home className="w-8 h-8 text-white" />
                    </div>
                </div>

                {/* Animated Loading Rings */}
                <div className="relative">
                    <div className="w-20 h-20 border-4 border-highland-100 border-t-highland-500 rounded-full animate-spin"></div>
                    <div className="absolute inset-2 w-12 h-12 border-4 border-dawn-100 border-t-dawn-500 rounded-full animate-spin animate-reverse"></div>
                </div>

                {/* Loading Message */}
                <div className="mt-8 text-center">
                    <h2 className="font-display text-xl font-semibold text-soil-900 mb-2">Rent Utopia</h2>
                    <p className="text-soil-600 animate-pulse">{message}</p>
                </div>

                {/* Loading Dots */}
                <div className="flex space-x-1 mt-6">
                    <div className="w-2 h-2 bg-highland-500 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-highland-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-highland-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
            </div>
        );
    }

    // Overlay loading
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40">
            <div className="bg-white rounded-lg p-8 max-w-sm mx-4 text-center shadow-2xl">
                {/* Spinner */}
                <div className="flex justify-center mb-4">
                    <div className="relative">
                        <Loader2 className="w-12 h-12 text-highland-500 animate-spin" />
                        <div className="absolute inset-0 w-12 h-12 border-2 border-highland-100 rounded-full"></div>
                    </div>
                </div>

                {/* Message */}
                <h3 className="text-lg font-semibold text-soil-900 mb-2">Please Wait</h3>
                <p className="text-soil-600 text-sm">{message}</p>
            </div>
        </div>
    );
};

// Skeleton Loader Component for property cards
export const PropertyCardSkeleton = () => {
    return (
        <div className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
            {/* Image Skeleton */}
            <div className="h-48 bg-soil-100"></div>

            {/* Content Skeleton */}
            <div className="p-4">
                {/* Price */}
                <div className="h-6 bg-soil-100 rounded w-24 mb-2"></div>

                {/* Title */}
                <div className="h-5 bg-soil-100 rounded w-3/4 mb-2"></div>

                {/* Location */}
                <div className="h-4 bg-soil-100 rounded w-1/2 mb-3"></div>

                {/* Features */}
                <div className="flex space-x-4 mb-3">
                    <div className="h-4 bg-soil-100 rounded w-16"></div>
                    <div className="h-4 bg-soil-100 rounded w-16"></div>
                    <div className="h-4 bg-soil-100 rounded w-16"></div>
                </div>

                {/* Button */}
                <div className="h-10 bg-soil-100 rounded w-full"></div>
            </div>
        </div>
    );
};

// Skeleton Loader for list items
export const ListItemSkeleton = ({ count = 5 }) => {
    return (
        <div className="space-y-3">
            {Array.from({ length: count }).map((_, index) => (
                <div key={index} className="flex items-center space-x-4 p-4 bg-white rounded-lg shadow animate-pulse">
                    <div className="w-12 h-12 bg-soil-100 rounded-full flex-shrink-0"></div>
                    <div className="flex-1">
                        <div className="h-4 bg-soil-100 rounded w-3/4 mb-2"></div>
                        <div className="h-3 bg-soil-100 rounded w-1/2"></div>
                    </div>
                    <div className="w-20 h-8 bg-soil-100 rounded"></div>
                </div>
            ))}
        </div>
    );
};

// Skeleton Loader for text content
export const TextSkeleton = ({ lines = 3 }) => {
    return (
        <div className="animate-pulse space-y-3">
            {Array.from({ length: lines }).map((_, index) => (
                <div
                    key={index}
                    className={`h-4 bg-soil-100 rounded ${
                        index === lines - 1 ? 'w-3/4' : 'w-full'
                    }`}
                ></div>
            ))}
        </div>
    );
};

// Button Loading State
export const LoadingButton = ({
                                  children,
                                  loading = false,
                                  disabled = false,
                                  className = '',
                                  variant = 'primary',
                                  ...props
                              }) => {
    const baseClasses = 'flex items-center justify-center space-x-2 px-4 py-2 rounded-md font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed';

    const variantClasses = {
        primary: 'bg-highland-500 text-white hover:bg-highland-600 disabled:hover:bg-highland-500',
        secondary: 'bg-soil-100 text-soil-900 hover:bg-soil-100 disabled:hover:bg-soil-100',
        outline: 'border border-soil-100 text-soil-600 hover:bg-soil-50 disabled:hover:bg-white'
    };

    return (
        <button
            className={`${baseClasses} ${variantClasses[variant]} ${className}`}
            disabled={loading || disabled}
            {...props}
        >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            <span>{children}</span>
        </button>
    );
};

// Progress Bar Component
export const ProgressBar = ({ progress = 0, showPercentage = true, className = '' }) => {
    return (
        <div className={`w-full ${className}`}>
            <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-soil-600">Loading...</span>
                {showPercentage && (
                    <span className="text-sm text-soil-600">{Math.round(progress)}%</span>
                )}
            </div>
            <div className="w-full bg-soil-100 rounded-full h-2">
                <div
                    className="bg-highland-500 h-2 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${Math.min(progress, 100)}%` }}
                ></div>
            </div>
        </div>
    );
};

export default LoadingSpinner;